import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';

/**
 * propKit.ts — the SHARED interactive-prop kit (GS4, graphics overhaul).
 *
 * Canvas painters + tiny mesh builders reused by the entity views (MoversView,
 * PickupsView, CarView aura) NOW and by the scenery pass (parked buses, the
 * London bus-ramp, the Napoli Vespa obstacle, boost pads) NEXT WAVE. Everything
 * here is display-only, procedural (zero downloads), and deterministic — art
 * variety comes from `propHash(index)`, NEVER from the layout RNG stream.
 *
 * CONVENTIONS (match the game's §6 mesh rules):
 *  - Vehicles front local +Z. Painted-box face order is three.js BoxGeometry
 *    order: [+x right, −x left, +y roof, −y floor, +z front, −z rear].
 *  - Side canvases are painted HORIZONTALLY SYMMETRIC (the +x face mirrors u
 *    vs the −x face — symmetry makes one canvas serve both without flipping).
 *  - Roof canvases: canvas VERTICAL axis = along the vehicle LENGTH (the +y
 *    box face maps u→width, v→length under flipY).
 *  - Wheels: `makeRoadWheel` returns a pivot whose `rotation.x` is the axle
 *    spin (axle along local X), matching the MoversView Spinner contract.
 */

/** Deterministic 0..1 hash for prop art variety (never the layout RNG). */
export function propHash(i: number): number {
  const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

export function propCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d') as CanvasRenderingContext2D];
}

/** Wrap a painted canvas as an sRGB texture (colour maps; NOT for bump/rough). */
export function propTexture(c: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/** Per-instance coat/paint tone jitter from an index hash (animals, crates). */
export function toneJitter(base: number, i: number, amount = 0.08): THREE.Color {
  const c = new THREE.Color(base);
  const hsl = { h: 0, s: 0, l: 0 };
  c.getHSL(hsl);
  c.setHSL(
    hsl.h + (propHash(i * 3 + 1) - 0.5) * amount * 0.5,
    Math.max(0, hsl.s + (propHash(i * 3 + 2) - 0.5) * amount),
    Math.max(0.05, hsl.l + (propHash(i * 3 + 3) - 0.5) * amount),
  );
  return c;
}

// ---------------------------------------------------------------------------
// Glow ring — the player aura (CarView) + reusable pad/marker glow.
// ---------------------------------------------------------------------------

let glowRingTex: THREE.CanvasTexture | null = null;

/**
 * A soft feathered ring on transparent black: peak at ~72% radius, feathered
 * both ways. One shared 64px canvas — tint via material.color, blend additive.
 */
export function glowRingTexture(): THREE.CanvasTexture {
  if (glowRingTex) return glowRingTex;
  const [c, x] = propCanvas(64, 64);
  const g = x.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.44, 'rgba(255,255,255,0)');
  g.addColorStop(0.6, 'rgba(255,255,255,0.35)');
  g.addColorStop(0.73, 'rgba(255,255,255,1)');
  g.addColorStop(0.86, 'rgba(255,255,255,0.3)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = g;
  x.fillRect(0, 0, 64, 64);
  glowRingTex = propTexture(c);
  return glowRingTex;
}

// ---------------------------------------------------------------------------
// Vehicle paint canvases (sides / front / rear / roof).
// ---------------------------------------------------------------------------

/** A colour canvas + its matching emissive mask (lit panes/lamps on black). */
export interface VehiclePaint {
  color: HTMLCanvasElement;
  emissive: HTMLCanvasElement;
}

export interface WindowBand {
  /** Band top, as a fraction of canvas height from the TOP. */
  top: number;
  /** Band height fraction. */
  height: number;
  /** Number of panes across. */
  panes: number;
  /** Fraction of panes lit (0..1, default 0.7). */
  lit?: number;
  /** Warm interior glow (true) vs cool glass (false, default true). */
  warm?: boolean;
  /** Mullion/frame colour (default near-black). */
  frame?: string;
}

export interface SidePaintOpts {
  w?: number;
  h?: number;
  /** Base body paint (CSS colour). */
  paint: string;
  /** Optional darker rocker/skirt band along the bottom ~10%. */
  skirt?: string;
  /** Window bands (fractions measured from the canvas TOP = vehicle top). */
  bands?: WindowBand[];
  /** Solid painted stripes: y/h are height fractions from the top. */
  stripes?: { y: number; h: number; color: string }[];
  /** Centered route/advert board (e.g. the bus's between-decks board). */
  board?: { text: string; y: number; h: number; bg: string; fg: string };
  /** 0..1 rust streaks + grime wash (Lagos danfo ≈ 0.5+). */
  grime?: number;
  /** Dark wheel-arch cutouts: x centers as width fractions. */
  arches?: number[];
  /** Arch radius as a height fraction (default 0.16). */
  archR?: number;
  seed?: number;
}

function paintPanelShading(
  x: CanvasRenderingContext2D,
  w: number,
  h: number,
  seed: number,
): void {
  // Subtle vertical tone variance so big painted faces don't read as flat fill.
  for (let i = 0; i < 12; i++) {
    const px = propHash(seed + i * 7) * w;
    const pw = 6 + propHash(seed + i * 13) * (w / 6);
    x.fillStyle = propHash(seed + i * 3) > 0.5 ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.05)';
    x.fillRect(px, 0, pw, h);
  }
}

function paintGrime(
  x: CanvasRenderingContext2D,
  w: number,
  h: number,
  amount: number,
  seed: number,
): void {
  if (amount <= 0) return;
  // Rust/grime: streaks bleeding DOWN from fittings + a dirty lower wash.
  const streaks = Math.round(amount * 14);
  for (let i = 0; i < streaks; i++) {
    const sx = propHash(seed + 31 + i * 5) * w;
    const sy = propHash(seed + 47 + i * 11) * h * 0.5;
    const len = (0.15 + propHash(seed + 61 + i * 3) * 0.5) * h;
    const grad = x.createLinearGradient(0, sy, 0, sy + len);
    const rusty = propHash(seed + 77 + i) > 0.45;
    grad.addColorStop(0, rusty ? `rgba(96,52,20,${0.35 * amount})` : `rgba(20,18,14,${0.3 * amount})`);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = grad;
    x.fillRect(sx, sy, 1.5 + propHash(seed + 91 + i) * 2.5, len);
  }
  const wash = x.createLinearGradient(0, h * 0.55, 0, h);
  wash.addColorStop(0, 'rgba(0,0,0,0)');
  wash.addColorStop(1, `rgba(28,22,14,${0.4 * amount})`);
  x.fillStyle = wash;
  x.fillRect(0, 0, w, h);
}

function paintBands(
  x: CanvasRenderingContext2D,
  e: CanvasRenderingContext2D,
  w: number,
  h: number,
  bands: WindowBand[],
  seed: number,
): void {
  for (let bi = 0; bi < bands.length; bi++) {
    const b = bands[bi];
    const top = b.top * h;
    const bh = b.height * h;
    x.fillStyle = b.frame ?? '#15171c';
    x.fillRect(0, top, w, bh);
    const gap = Math.max(2, w * 0.012);
    const paneW = (w - gap * (b.panes + 1)) / b.panes;
    for (let i = 0; i < b.panes; i++) {
      const lit = propHash(seed + bi * 37 + i * 7) < (b.lit ?? 0.7);
      const warm = b.warm ?? true;
      const px = gap + i * (paneW + gap);
      // Glass base with a sky-catch diagonal.
      x.fillStyle = lit ? (warm ? '#f5c86e' : '#bcd8ee') : '#232b36';
      x.fillRect(px, top + gap, paneW, bh - gap * 2);
      x.fillStyle = 'rgba(255,255,255,0.14)';
      x.beginPath();
      x.moveTo(px, top + gap);
      x.lineTo(px + paneW * 0.45, top + gap);
      x.lineTo(px, top + bh - gap);
      x.closePath();
      x.fill();
      if (lit) {
        e.fillStyle = warm ? '#ffd98c' : '#cfe6f8';
        e.fillRect(px, top + gap, paneW, bh - gap * 2);
      }
    }
  }
}

/** Paint one vehicle flank (window band + mullions, stripes, board, grime,
 *  wheel arches). Returns colour + emissive canvases (lit panes only). */
export function vehicleSideCanvas(opts: SidePaintOpts): VehiclePaint {
  const w = opts.w ?? 256;
  const h = opts.h ?? 128;
  const seed = opts.seed ?? 1;
  const [c, x] = propCanvas(w, h);
  const [ce, e] = propCanvas(w, h);
  e.fillStyle = '#000';
  e.fillRect(0, 0, w, h);
  x.fillStyle = opts.paint;
  x.fillRect(0, 0, w, h);
  paintPanelShading(x, w, h, seed);
  if (opts.skirt) {
    x.fillStyle = opts.skirt;
    x.fillRect(0, h * 0.88, w, h * 0.12);
  }
  for (const s of opts.stripes ?? []) {
    x.fillStyle = s.color;
    x.fillRect(0, s.y * h, w, s.h * h);
  }
  paintBands(x, e, w, h, opts.bands ?? [], seed);
  if (opts.board) {
    const bw = w * 0.34;
    const bh = opts.board.h * h;
    const bx = (w - bw) / 2;
    const by = opts.board.y * h;
    x.fillStyle = opts.board.bg;
    x.fillRect(bx, by, bw, bh);
    x.fillStyle = opts.board.fg;
    x.font = `bold ${Math.round(bh * 0.62)}px system-ui, sans-serif`;
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    x.fillText(opts.board.text, w / 2, by + bh / 2);
  }
  paintGrime(x, w, h, opts.grime ?? 0, seed);
  // Wheel arches LAST — the cutout must never be painted over.
  const archR = (opts.archR ?? 0.16) * h;
  for (const ax of opts.arches ?? []) {
    x.fillStyle = '#0b0c0f';
    x.beginPath();
    x.arc(ax * w, h, archR * 1.9, Math.PI, 0);
    x.fill();
  }
  return { color: c, emissive: ce };
}

export interface FrontPaintOpts {
  w?: number;
  h?: number;
  paint: string;
  /** Windscreen panes: fractions from the top; split > 1 draws mullions. */
  screens?: { top: number; height: number; split?: number; inset?: number }[];
  /** Lit destination blind (the London bus's "23 · WESTMINSTER"). */
  blind?: { text: string; y: number; h: number };
  /** Headlamps: painted + emissive. x is a width fraction of the centre pair. */
  lamps?: { y: number; r: number; spread: number; color?: string };
  /** Dark bumper band along the bottom. */
  bumper?: { h: number; color?: string };
  /** Grille slats between the lamps. */
  grille?: { y: number; h: number };
  grime?: number;
  seed?: number;
}

/** Paint a vehicle face (front or rear): windscreen + lamps + bumper + blind. */
export function vehicleFrontCanvas(opts: FrontPaintOpts): VehiclePaint {
  const w = opts.w ?? 128;
  const h = opts.h ?? 128;
  const seed = opts.seed ?? 2;
  const [c, x] = propCanvas(w, h);
  const [ce, e] = propCanvas(w, h);
  e.fillStyle = '#000';
  e.fillRect(0, 0, w, h);
  x.fillStyle = opts.paint;
  x.fillRect(0, 0, w, h);
  paintPanelShading(x, w, h, seed);
  for (const s of opts.screens ?? []) {
    const inset = (s.inset ?? 0.08) * w;
    const top = s.top * h;
    const sh = s.height * h;
    x.fillStyle = '#101820';
    x.fillRect(inset - 2, top - 2, w - inset * 2 + 4, sh + 4);
    const split = s.split ?? 1;
    const paneW = (w - inset * 2 - (split - 1) * 3) / split;
    for (let i = 0; i < split; i++) {
      const px = inset + i * (paneW + 3);
      x.fillStyle = '#22303e';
      x.fillRect(px, top, paneW, sh);
      x.fillStyle = 'rgba(190,220,240,0.22)';
      x.beginPath();
      x.moveTo(px, top);
      x.lineTo(px + paneW * 0.6, top);
      x.lineTo(px, top + sh);
      x.closePath();
      x.fill();
    }
  }
  if (opts.blind) {
    const bw = w * 0.7;
    const bx = (w - bw) / 2;
    const by = opts.blind.y * h;
    const bh = opts.blind.h * h;
    x.fillStyle = '#1a1c14';
    x.fillRect(bx - 2, by - 2, bw + 4, bh + 4);
    x.fillStyle = '#f8e6a8';
    x.fillRect(bx, by, bw, bh);
    x.fillStyle = '#2a2408';
    x.font = `bold ${Math.round(bh * 0.7)}px system-ui, sans-serif`;
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    x.fillText(opts.blind.text, w / 2, by + bh / 2);
    e.fillStyle = '#c8b070';
    e.fillRect(bx, by, bw, bh);
  }
  if (opts.grille) {
    const gy = opts.grille.y * h;
    const gh = opts.grille.h * h;
    x.fillStyle = '#14161a';
    x.fillRect(w * 0.3, gy, w * 0.4, gh);
    x.fillStyle = '#2c3038';
    for (let i = 1; i < 4; i++) x.fillRect(w * 0.3, gy + (gh * i) / 4, w * 0.4, 1.5);
  }
  if (opts.lamps) {
    const { y, r, spread } = opts.lamps;
    const col = opts.lamps.color ?? '#ffe9b0';
    for (const sx of [-1, 1]) {
      const lx = w / 2 + sx * spread * w * 0.5;
      x.fillStyle = '#0e0f12';
      x.beginPath();
      x.arc(lx, y * h, r * h * 1.25, 0, Math.PI * 2);
      x.fill();
      x.fillStyle = col;
      x.beginPath();
      x.arc(lx, y * h, r * h, 0, Math.PI * 2);
      x.fill();
      e.fillStyle = col;
      e.beginPath();
      e.arc(lx, y * h, r * h, 0, Math.PI * 2);
      e.fill();
    }
  }
  if (opts.bumper) {
    x.fillStyle = opts.bumper.color ?? '#191b1f';
    x.fillRect(0, h * (1 - opts.bumper.h), w, h * opts.bumper.h);
  }
  paintGrime(x, w, h, opts.grime ?? 0, seed);
  return { color: c, emissive: ce };
}

export interface RoofPaintOpts {
  w?: number;
  h?: number;
  paint: string;
  /** Lengthwise vent/rib lines (canvas-vertical = along the vehicle). */
  vents?: number;
  ventColor?: string;
  /** Crosswise panel seams. */
  seams?: number;
  grime?: number;
  seed?: number;
}

/** Roof-cap treatment — the face the chase camera actually stares at. */
export function vehicleRoofCanvas(opts: RoofPaintOpts): HTMLCanvasElement {
  const w = opts.w ?? 128;
  const h = opts.h ?? 256;
  const seed = opts.seed ?? 3;
  const [c, x] = propCanvas(w, h);
  x.fillStyle = opts.paint;
  x.fillRect(0, 0, w, h);
  paintPanelShading(x, w, h, seed + 5);
  // Soft edge shading so the roof reads as a curved cap, not a flat lid.
  const edge = x.createLinearGradient(0, 0, w, 0);
  edge.addColorStop(0, 'rgba(0,0,0,0.28)');
  edge.addColorStop(0.18, 'rgba(0,0,0,0)');
  edge.addColorStop(0.82, 'rgba(0,0,0,0)');
  edge.addColorStop(1, 'rgba(0,0,0,0.28)');
  x.fillStyle = edge;
  x.fillRect(0, 0, w, h);
  const vents = opts.vents ?? 0;
  for (let i = 0; i < vents; i++) {
    const vx = (w * (i + 1)) / (vents + 1);
    x.fillStyle = opts.ventColor ?? 'rgba(20,22,26,0.65)';
    x.fillRect(vx - 1.5, h * 0.12, 3, h * 0.76);
  }
  const seams = opts.seams ?? 0;
  for (let i = 1; i <= seams; i++) {
    x.fillStyle = 'rgba(0,0,0,0.22)';
    x.fillRect(0, (h * i) / (seams + 1), w, 1.5);
  }
  paintGrime(x, w, h, (opts.grime ?? 0) * 0.7, seed + 9);
  return c;
}

// ---------------------------------------------------------------------------
// Painted-box material assembly (the mover/parked-vehicle body pattern).
// ---------------------------------------------------------------------------

export interface PaintedBoxOpts {
  side: VehiclePaint;
  front: VehiclePaint;
  rear: VehiclePaint;
  roof: HTMLCanvasElement;
  roughness?: number;
  metalness?: number;
  /** Window-glow strength (0 kills the emissive maps). Default 0.55. */
  emissiveIntensity?: number;
}

/**
 * Build the 6-material array for a painted vehicle body box, in BoxGeometry
 * face order [+x, −x, +y, −y, +z(front), −z(rear)]. The two sides share ONE
 * material (side canvases are painted symmetric); the floor is flat dark.
 */
export function paintedBoxMaterials(opts: PaintedBoxOpts): THREE.MeshStandardMaterial[] {
  const rough = opts.roughness ?? 0.55;
  const metal = opts.metalness ?? 0.08;
  const glow = opts.emissiveIntensity ?? 0.55;
  const mk = (p: VehiclePaint): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({
      map: propTexture(p.color),
      emissive: 0xffffff,
      emissiveMap: propTexture(p.emissive),
      emissiveIntensity: glow,
      roughness: rough,
      metalness: metal,
    });
  const side = mk(opts.side);
  const roof = new THREE.MeshStandardMaterial({
    map: propTexture(opts.roof),
    roughness: Math.min(0.9, rough + 0.15),
    metalness: metal,
  });
  const floor = new THREE.MeshStandardMaterial({ color: 0x0c0d10, roughness: 0.95 });
  return [side, side, roof, floor, mk(opts.front), mk(opts.rear)];
}

// ---------------------------------------------------------------------------
// The LONDON DOUBLE-DECKER canvas set (mover now; parked buses + the London
// ramp-dressed-as-bus reuse it next wave).
// ---------------------------------------------------------------------------

export interface BusCanvasSet {
  side: VehiclePaint;
  front: VehiclePaint;
  rear: VehiclePaint;
  roof: HTMLCanvasElement;
}

/** The classic red RM look: two lit window decks, cream between-decks band,
 *  route board, dark skirt + arches, cream roof with a centre vent line. */
export function doubleDeckerCanvases(opts?: {
  route?: string;
  dest?: string;
  seed?: number;
}): BusCanvasSet {
  const route = opts?.route ?? '23';
  const dest = opts?.dest ?? 'WESTMINSTER';
  const seed = opts?.seed ?? 11;
  const red = '#b81a2b';
  return {
    side: vehicleSideCanvas({
      w: 512,
      h: 256,
      paint: red,
      skirt: '#5c0f18',
      bands: [
        { top: 0.08, height: 0.2, panes: 7, lit: 0.8, warm: true },
        { top: 0.56, height: 0.2, panes: 7, lit: 0.75, warm: true },
      ],
      stripes: [{ y: 0.46, h: 0.07, color: '#e8e2d0' }],
      board: { text: `${route} · ${dest}`, y: 0.335, h: 0.1, bg: '#f0ead6', fg: '#7a1220' },
      grime: 0.22,
      arches: [0.18, 0.82],
      archR: 0.09, // the bus face is TALL — keep arch world-radius ≈ wheel+trim
      seed,
    }),
    front: vehicleFrontCanvas({
      w: 128,
      h: 192,
      paint: red,
      screens: [
        { top: 0.1, height: 0.14, split: 2 }, // top-deck front window
        { top: 0.46, height: 0.17, split: 1 }, // driver's windscreen
      ],
      blind: { text: `${route} ${dest}`, y: 0.3, h: 0.07 },
      lamps: { y: 0.82, r: 0.035, spread: 0.56 },
      bumper: { h: 0.08 },
      grille: { y: 0.68, h: 0.09 },
      grime: 0.15,
      seed: seed + 1,
    }),
    rear: vehicleFrontCanvas({
      w: 128,
      h: 192,
      paint: red,
      screens: [
        { top: 0.1, height: 0.14, split: 1 },
        { top: 0.5, height: 0.2, split: 1, inset: 0.16 },
      ],
      lamps: { y: 0.85, r: 0.035, spread: 0.6, color: '#ff3540' },
      bumper: { h: 0.08 },
      grime: 0.3,
      seed: seed + 2,
    }),
    roof: vehicleRoofCanvas({
      paint: '#ded8c6',
      vents: 1,
      seams: 3,
      grime: 0.35,
      seed: seed + 3,
    }),
  };
}

// ---------------------------------------------------------------------------
// Wheels — visible from the chase cam's high angle.
// ---------------------------------------------------------------------------

export interface PropWheel {
  /** Spin this pivot's rotation.x (axle along local X). */
  pivot: THREE.Group;
  radius: number;
}

let tyreMatShared: THREE.MeshStandardMaterial | null = null;

/**
 * A real road wheel: dark tyre cylinder + a lighter hub-cap core whose side
 * faces read from above/side (the old stub wheels vanished because tyre-dark
 * met road-dark with nothing between). Axle along local X; spin via
 * `pivot.rotation.x`.
 */
export function makeRoadWheel(
  radius: number,
  width: number,
  opts?: { hub?: number; tyre?: number },
): PropWheel {
  const pivot = new THREE.Group();
  tyreMatShared ??= new THREE.MeshStandardMaterial({ color: 0x17181c, roughness: 0.92 });
  const tyreMat = opts?.tyre
    ? new THREE.MeshStandardMaterial({ color: opts.tyre, roughness: 0.92 })
    : tyreMatShared;
  const tyre = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, width, 14), tyreMat);
  tyre.rotation.z = Math.PI / 2;
  pivot.add(tyre);
  const hub = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 0.52, radius * 0.52, width * 1.06, 10),
    new THREE.MeshStandardMaterial({
      color: opts?.hub ?? 0x9aa1ab,
      roughness: 0.35,
      metalness: 0.6,
    }),
  );
  hub.rotation.z = Math.PI / 2;
  pivot.add(hub);
  return { pivot, radius };
}

// ---------------------------------------------------------------------------
// The complete low-poly VESPA (Napoli mover WITH rider now; the parked Napoli
// obstacle consumes the same build next wave with { rider:false, stand:true }).
// ---------------------------------------------------------------------------

export interface VespaProp {
  group: THREE.Group;
  /** Front + rear wheels (Spinner contract: spin pivot.rotation.x). */
  wheels: PropWheel[];
}

const vStd = (color: number, rough = 0.35): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: 0.12 });

/**
 * A real Vespa silhouette fronting +Z: leg shield (curved apron), floorboard,
 * bulged rear body + engine cheeks, saddle, chrome handlebar with a ROUND
 * headlamp + two mirrors, front fender over a real wheel — and optionally a
 * helmeted rider (helmet in the body colour, the M50 rule) or a kickstand.
 */
export function buildVespaProp(opts?: {
  body?: number;
  rider?: boolean;
  stand?: boolean;
}): VespaProp {
  const body = opts?.body ?? 0x9fd8c4;
  const group = new THREE.Group();
  const paint = vStd(body, 0.3);
  const dark = vStd(0x1c1e24, 0.8);
  const chrome = new THREE.MeshStandardMaterial({
    color: 0xd9dde4,
    roughness: 0.18,
    metalness: 0.9,
  });
  const lamp = new THREE.MeshBasicMaterial({ color: 0xfff2c8 });

  // Rear body — the classic bulge: a squashed sphere + two engine cheeks.
  const rear = new THREE.Mesh(new THREE.SphereGeometry(0.5, 12, 9), paint);
  rear.scale.set(0.72, 0.62, 1.05);
  rear.position.set(0, 0.62, -0.5);
  group.add(rear);
  for (const sx of [-1, 1]) {
    const cheek = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), paint);
    cheek.scale.set(0.7, 0.8, 1.1);
    cheek.position.set(sx * 0.22, 0.5, -0.52);
    group.add(cheek);
  }
  // Saddle.
  const saddle = new THREE.Mesh(new RoundedBoxGeometry(0.34, 0.14, 0.62, 2, 0.05), dark);
  saddle.position.set(0, 0.95, -0.38);
  group.add(saddle);
  // Floorboard + central spine down to it.
  const board = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.07, 0.62), paint);
  board.position.set(0, 0.32, 0.12);
  group.add(board);
  const spine = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.3, 0.5), paint);
  spine.rotation.x = 0.65;
  spine.position.set(0, 0.44, -0.12);
  group.add(spine);
  // Leg shield — a real curved apron (partial cylinder shell), tilted back a touch.
  const shieldGeo = new THREE.CylinderGeometry(0.34, 0.4, 0.78, 12, 1, true, -Math.PI * 0.42, Math.PI * 0.84);
  const shield = new THREE.Mesh(
    shieldGeo,
    new THREE.MeshStandardMaterial({
      color: body,
      roughness: 0.3,
      metalness: 0.12,
      side: THREE.DoubleSide,
    }),
  );
  shield.rotation.x = -0.1;
  shield.position.set(0, 0.72, 0.32);
  group.add(shield);
  // Steering column + handlebar + round headlamp + mirrors.
  const column = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.66, 6), chrome);
  column.rotation.x = 0.3;
  column.position.set(0, 0.92, 0.55);
  group.add(column);
  const bars = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.52, 6), chrome);
  bars.rotation.z = Math.PI / 2;
  bars.position.set(0, 1.22, 0.46);
  group.add(bars);
  for (const sx of [-1, 1]) {
    const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.036, 0.036, 0.12, 6), dark);
    grip.rotation.z = Math.PI / 2;
    grip.position.set(sx * 0.24, 1.22, 0.46);
    group.add(grip);
    const stalk = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.16, 5), chrome);
    stalk.rotation.z = sx * 0.6;
    stalk.position.set(sx * 0.18, 1.34, 0.44);
    group.add(stalk);
    const mirror = new THREE.Mesh(new THREE.SphereGeometry(0.045, 8, 6), chrome);
    mirror.scale.set(1, 1.3, 0.4);
    mirror.position.set(sx * 0.23, 1.42, 0.44);
    group.add(mirror);
  }
  const headlampRing = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.06, 10), chrome);
  headlampRing.rotation.x = Math.PI / 2 - 0.3;
  headlampRing.position.set(0, 1.14, 0.6);
  group.add(headlampRing);
  const headlamp = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 6), lamp);
  headlamp.position.set(0, 1.14, 0.63);
  group.add(headlamp);
  // Front fender hugging the wheel (theta 0.1π..0.9π = the TOP arc once
  // rotateZ swings the cylinder axis onto X).
  const fenderGeo = new THREE.CylinderGeometry(0.32, 0.32, 0.16, 10, 1, true, Math.PI * 0.1, Math.PI * 0.8);
  fenderGeo.rotateZ(Math.PI / 2);
  const fender = new THREE.Mesh(
    fenderGeo,
    new THREE.MeshStandardMaterial({
      color: body,
      roughness: 0.3,
      metalness: 0.12,
      side: THREE.DoubleSide,
    }),
  );
  fender.rotation.x = -0.15;
  fender.position.set(0, 0.36, 0.78);
  group.add(fender);
  // Wheels (Vespa rims wear the body colour hub).
  const wheels: PropWheel[] = [];
  const front = makeRoadWheel(0.26, 0.13, { hub: body });
  front.pivot.position.set(0, 0.26, 0.78);
  group.add(front.pivot);
  wheels.push(front);
  const rearWheel = makeRoadWheel(0.26, 0.13, { hub: body });
  rearWheel.pivot.position.set(0, 0.26, -0.62);
  group.add(rearWheel.pivot);
  wheels.push(rearWheel);
  if (opts?.stand) {
    const stand = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.3, 5), chrome);
    stand.rotation.z = 0.45;
    stand.position.set(0.12, 0.16, -0.35);
    stand.userData.keepOnSwap = true;
    group.add(stand);
  }
  if (opts?.rider ?? true) {
    const suit = vStd(0x3a4a6a, 0.8);
    const torso = new THREE.Mesh(new RoundedBoxGeometry(0.4, 0.56, 0.3, 2, 0.09), suit);
    torso.rotation.x = 0.22; // leaning into the bars
    torso.position.set(0, 1.32, -0.32);
    torso.userData.keepOnSwap = true;
    group.add(torso);
    for (const sx of [-1, 1]) {
      const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.52, 6), suit);
      arm.rotation.x = 1.05;
      arm.rotation.z = sx * 0.3;
      arm.position.set(sx * 0.2, 1.32, 0.05);
      arm.userData.keepOnSwap = true;
      group.add(arm);
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.42, 6), dark);
      leg.rotation.x = 0.5;
      leg.position.set(sx * 0.16, 0.72, -0.1);
      leg.userData.keepOnSwap = true;
      group.add(leg);
    }
    const helmet = new THREE.Mesh(new THREE.SphereGeometry(0.19, 10, 8), paint);
    helmet.position.set(0, 1.74, -0.24);
    helmet.userData.keepOnSwap = true;
    group.add(helmet);
    const visor = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 6), dark);
    visor.scale.set(0.9, 0.6, 0.7);
    visor.position.set(0, 1.72, -0.1);
    visor.userData.keepOnSwap = true;
    group.add(visor);
  }
  return { group, wheels };
}

// ---------------------------------------------------------------------------
// Animal shaping helpers (goats, guanacos — and any future critter).
// ---------------------------------------------------------------------------

/** A soft-bevelled box (smooth normals — kills the voxel read). */
export function roundedBoxGeo(w: number, h: number, d: number, r: number): THREE.BufferGeometry {
  return new RoundedBoxGeometry(w, h, d, 2, Math.min(r, Math.min(w, h, d) * 0.45));
}

/**
 * A 4-sided tapered wedge pointing +Z (snouts, tails): rTip at the far end.
 * Pre-rotated so the flats face up/down/side.
 */
export function snoutWedgeGeo(rTip: number, rBase: number, len: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(rTip, rBase, len, 4, 1);
  g.rotateY(Math.PI / 4);
  g.rotateX(Math.PI / 2);
  return g;
}

/** A tapered limb/neck segment along +Y (top radius first). */
export function taperedGeo(
  rTop: number,
  rBottom: number,
  len: number,
  seg = 6,
): THREE.BufferGeometry {
  return new THREE.CylinderGeometry(rTop, rBottom, len, seg, 1);
}

/** A flattened ear fin pointing +Y. */
export function earFinGeo(r: number, len: number): THREE.BufferGeometry {
  const g = new THREE.ConeGeometry(r, len, 5);
  g.scale(1, 1, 0.45);
  return g;
}

let furTex: THREE.CanvasTexture | null = null;

/** Shared neutral fur-mottle map — near-white with soft darker blotches; tint
 *  the coat via material.color so ONE texture serves every animal. */
export function furMottleTexture(): THREE.CanvasTexture {
  if (furTex) return furTex;
  const [c, x] = propCanvas(64, 64);
  x.fillStyle = '#f2efe9';
  x.fillRect(0, 0, 64, 64);
  for (let i = 0; i < 46; i++) {
    const bx = propHash(i * 5 + 1) * 64;
    const by = propHash(i * 5 + 2) * 64;
    const r = 2 + propHash(i * 5 + 3) * 6;
    const dark = 0.06 + propHash(i * 5 + 4) * 0.12;
    const g = x.createRadialGradient(bx, by, 0, bx, by, r);
    g.addColorStop(0, `rgba(70,58,44,${dark})`);
    g.addColorStop(1, 'rgba(70,58,44,0)');
    x.fillStyle = g;
    x.fillRect(bx - r, by - r, r * 2, r * 2);
  }
  furTex = propTexture(c);
  furTex.wrapS = furTex.wrapT = THREE.RepeatWrapping;
  return furTex;
}
